/**
 * Authentication URL Configuration
 * 
 * Centralized configuration for all authentication-related redirects.
 * Ensures consistent URL handling across password recovery, magic links, and other auth flows.
 */

/**
 * Get the password recovery redirect URL
 * 
 * Returns the full URL where password recovery emails should redirect to.
 * This is the canonical password reset page for the admin application.
 * 
 * @returns {string} Full URL to the partner password reset page
 */
export const getPasswordRecoveryRedirect = (): string => {
  // CRITICAL: This MUST point to /partner-password-reset for ALL password recovery flows
  // See: ADMIN_OFFER_EDIT_CAPABILITIES.md for redirect requirements
  return `${window.location.origin}/partner-password-reset`;
};

/**
 * Password recovery redirect URL constant
 * 
 * Use this constant when calling resetPasswordForEmail() to ensure
 * all password recovery emails redirect to the correct page.
 * 
 * @constant
 */
export const PASSWORD_RECOVERY_REDIRECT = getPasswordRecoveryRedirect();

/**
 * Magic link confirmation URL
 * 
 * This URL is used for magic link authentication flows.
 * Should NOT be used for password recovery - use PASSWORD_RECOVERY_REDIRECT instead.
 * 
 * @constant
 */
export const MAGIC_LINK_CONFIRMATION_URL = `${window.location.origin}/magic-link-confirmation`;

/**
 * Supabase URL Configuration Checklist
 * 
 * Required settings in Supabase Dashboard → Authentication → URL Configuration:
 * 
 * 1. Site URL: https://admin.travelheroes.at
 * 
 * 2. Redirect URLs (must include BOTH with and without trailing slash):
 *    - https://admin.travelheroes.at/partner-password-reset
 *    - https://admin.travelheroes.at/partner-password-reset/
 *    - https://admin.travelheroes.at/magic-link-confirmation
 *    - https://admin.travelheroes.at/magic-link-confirmation/
 * 
 * 3. Email Templates:
 *    - Confirm signup: Uses {{ .ConfirmationURL }}
 *    - Magic Link: Uses {{ .ConfirmationURL }}
 *    - Change Email Address: Uses {{ .ConfirmationURL }}
 *    - Reset Password: MUST use redirect_to parameter (set in code via PASSWORD_RECOVERY_REDIRECT)
 */